import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box, Flex, Text, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalCloseButton, Spinner,
  IconButton, Image, useToast,
} from "@chakra-ui/react";
import { ViewIcon, ChevronLeftIcon, ChevronRightIcon, WarningIcon } from "@chakra-ui/icons";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import DateRangeFilter from "components/DateRangeFilter/DateRangeFilter";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";
import { useDateFilter } from "Hooks/useDateFilter";
import { getCurrentDate, getFirstDayOfMonth, parseDate } from "Utils/dateUtils";

const RED    = "#C0392B";
const BORDER = "#EBEBEB";

const S3_PRESIGNED_BASE = "https://s3service.sconexsoft.com/s3bucket/presigned-url";
const S3_BUCKET_NAME    = "imageinsightimagesbeverages";


const buildDateRangeBody = (from, to) => {
  const { year: fy, month: fm, day: fd } = parseDate(from);
  const { year: ty, month: tm, day: td } = parseDate(to);
  return {
    from: { date: fd, month: fm, year: fy },
    to:   { date: td, month: tm, year: ty },
  };
};


const validateDateRange = (from, to) => {
  if (!from || !to) return "Please select both start and end dates.";
  if (from > to)    return "Start date cannot be after end date.";
  return null;
};


const fetchPresignedUrl = async (s3Path, month, year, cache) => {
  if (!s3Path) return null;

  const cacheKey = `${s3Path}_${month}_${year}`;
  if (cache[cacheKey]) return cache[cacheKey];

  const endpoint =
    `${S3_PRESIGNED_BASE}` +
    `?fileName=${encodeURIComponent(s3Path)}` +
    `&bucketName=${S3_BUCKET_NAME}` +
    `&operation=download` +
    `&month=${month}` +
    `&year=${year}` +
    `&ResponseContentDisposition=attachment`;

  try {
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const url = (await response.text()).trim();
    cache[cacheKey] = url;
    return url;
  } catch (err) {
    console.error("Failed to fetch presigned URL for:", s3Path, err);
    return null;
  }
};

// ─── Action cell renderer ─────────────────────────────────────────────────────
const ActionRenderer = ({ data, context }) => (
  <Button
    size="sm"
    variant="outline"
    colorScheme="red"
    leftIcon={<ViewIcon />}
    borderRadius="6px"
    fontSize="12px"
    fontWeight={600}
    h="30px"
    px={3}
    _hover={{ bg: RED, color: "white", borderColor: RED }}
    onClick={() => context?.onViewDetails?.(data)}
  >
    View
  </Button>
);

// ─── Summary grid column definitions ─────────────────────────────────────────
const buildColumnDefs = () => [
  {
    headerName: "Store ID",
    field: "storeId",
    minWidth: 130,
    maxWidth: 170,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { fontWeight: 600, color: "#2D3748", display: "flex", alignItems: "center" },
  },
  {
    headerName: "Store Name",
    field: "storeName",
    minWidth: 200,
    flex: 2,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { color: "#1A202C", display: "flex", alignItems: "center" },
  },
  {
    headerName: "Total Images",
    field: "totalImages",
    minWidth: 130,
    maxWidth: 160,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellStyle: { fontWeight: 600, color: RED, display: "flex", alignItems: "center" },
  },
  {
    headerName: "Action",
    field: "action",
    minWidth: 110,
    maxWidth: 130,
    sortable: false,
    filter: false,
    resizable: false,
    pinned: "right",
    cellRenderer: ActionRenderer,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
];

// ─── Shelf counts column definitions ─────────────────────────────────────────
const shelfColumnDefs = [
  {
    headerName: "Product Name",
    field: "productName",
    minWidth: 200,
    flex: 2,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { display: "flex", alignItems: "center", color: "#1A202C" },
  },
  {
    headerName: "Count",
    field: "count",
    minWidth: 100,
    maxWidth: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellStyle: { display: "flex", alignItems: "center", fontWeight: 600, color: RED },
  },
];

// ─── BrokenImageFallback ──────────────────────────────────────────────────────
const BrokenImageFallback = ({ message = "Image not available" }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    gap={2}
    minH="200px"
    color="gray.400"
  >
    <WarningIcon boxSize={8} />
    <Text fontSize="sm">{message}</Text>
  </Flex>
);

// ─── Detail modal ─────────────────────────────────────────────────────────────
function DetailModal({
  isOpen,
  onClose,
  selectedRow,
  detailsLoading,
  detailsData,        
  toDate,             
}) {
  const [currentIndex,  setCurrentIndex]  = useState(0);
  const [presignedUrl,  setPresignedUrl]  = useState(null);
  const [imageLoading,  setImageLoading]  = useState(false);
  const [imageError,    setImageError]    = useState(false);

  const urlCache = useRef({});

  const totalItems  = detailsData?.length ?? 0;
  const currentItem = detailsData?.[currentIndex];

  // Shelf counts rows for AG Grid
  const shelfRows = currentItem?.shelfCounts
    ? Object.entries(currentItem.shelfCounts).map(([productName, count]) => ({
        productName,
        count,
      }))
    : [];

  
  const loadImage = useCallback(async (item, date) => {
    const s3Path = item?.imagePath;
    if (!s3Path) {
      setPresignedUrl(null);
      setImageLoading(false);
      setImageError(false);
      return;
    }

    setImageLoading(true);
    setImageError(false);
    setPresignedUrl(null);

    try {
      const [year, month] = (date || "").split("-").map(Number);
      const url = await fetchPresignedUrl(s3Path, month, year, urlCache.current);
      if (url) {
        setPresignedUrl(url);
      } else {
        setImageError(true);
      }
    } catch {
      setImageError(true);
    } finally {
      setImageLoading(false);
    }
  }, []);

  
  useEffect(() => {
    if (isOpen && currentItem) {
      loadImage(currentItem, toDate);
    }
  }, [isOpen, currentItem, toDate, loadImage]);

  
  useEffect(() => {
    if (!isOpen) {
      setCurrentIndex(0);
      setPresignedUrl(null);
      setImageLoading(false);
      setImageError(false);
      urlCache.current = {};
    }
  }, [isOpen]);

  const handlePrev = () => {
    setCurrentIndex((p) => (p === 0 ? totalItems - 1 : p - 1));
  };

  const handleNext = () => {
    setCurrentIndex((p) => (p === totalItems - 1 ? 0 : p + 1));
  };

  const handleClose = () => {
    setCurrentIndex(0);
    setPresignedUrl(null);
    setImageLoading(false);
    setImageError(false);
    urlCache.current = {};
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="12px" maxH="90vh">

        {/* Header */}
        <ModalHeader
          fontSize="md"
          fontWeight={700}
          color="gray.800"
          borderBottom={`1px solid ${BORDER}`}
          pb={3}
        >
          Image Details
          {selectedRow?.storeName && (
            <Text as="span" fontWeight={400} color="gray.500" ml={2} fontSize="sm">
              — {selectedRow.storeName}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />

        {/* Body */}
        <ModalBody py={5}>
          {detailsLoading ? (
            <Flex justify="center" align="center" h="300px" direction="column" gap={3}>
              <Spinner size="xl" color="red.500" thickness="4px" />
              <Text fontSize="sm" color="gray.500">Loading image details…</Text>
            </Flex>
          ) : totalItems === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column" gap={2}>
              <WarningIcon boxSize={8} color="gray.300" />
              <Text fontSize="sm" color="gray.500">No image details found for this store.</Text>
            </Flex>
          ) : (
            <Flex direction="column" gap={6}>

              {/* ── Image card ── */}
              <Box
                border={`1px solid ${BORDER}`}
                borderRadius="12px"
                overflow="hidden"
                bg="gray.50"
              >
                <Box borderBottom={`1px solid ${BORDER}`}>
                  {/* Sub-category label */}
                  <Text
                    fontSize="md"
                    fontWeight={600}
                    color="white"
                    textAlign="center"
                    py={2}
                    borderBottom={`1px solid ${BORDER}`}
                    bg={RED}
                  >
                    {currentItem?.subCategoryName || "Image"}
                  </Text>

                  {/* Image area */}
                  <Flex justify="center" align="center" minH="420px" p={3} bg="white">
                    {imageLoading ? (
                      <Flex direction="column" align="center" gap={3}>
                        <Spinner size="lg" color="red.500" thickness="3px" />
                        <Text fontSize="xs" color="gray.400">Loading image...</Text>
                      </Flex>
                    ) : imageError ? (
                      <BrokenImageFallback message="Unable to load image. Please try again." />
                    ) : presignedUrl ? (
                      <Image
                        src={presignedUrl}
                        alt={currentItem?.fileName || "Store image"}
                        maxW="100%"
                        maxH="420px"
                        objectFit="contain"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <BrokenImageFallback message="No image path provided." />
                    )}
                  </Flex>
                </Box>

                {/* File name strip */}
                {currentItem?.fileName && (
                  <Flex
                    px={4} py={2}
                    bg="white"
                    borderBottom={`1px solid ${BORDER}`}
                    align="center"
                    gap={2}
                  >
                    <Text fontSize="xs" color="gray.500" fontWeight={500}>
                      File:
                    </Text>
                    <Text fontSize="xs" color="gray.700" fontWeight={600} wordBreak="break-all">
                      {currentItem.fileName}
                    </Text>
                  </Flex>
                )}

                {/* Chevron navigation */}
                {totalItems > 1 && (
                  <Flex
                    justify="flex-end"
                    align="center"
                    gap={2}
                    px={4}
                    py={2}
                    borderTop={`1px solid ${BORDER}`}
                    bg="white"
                  >
                    <IconButton
                      icon={<ChevronLeftIcon boxSize={5} />}
                      onClick={handlePrev}
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      aria-label="Previous image"
                      _hover={{ bg: "gray.100" }}
                    />
                    <Text
                      fontSize="xs"
                      fontWeight={600}
                      color="gray.600"
                      minW="32px"
                      textAlign="center"
                    >
                      {currentIndex + 1} / {totalItems}
                    </Text>
                    <IconButton
                      icon={<ChevronRightIcon boxSize={5} />}
                      onClick={handleNext}
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      aria-label="Next image"
                      _hover={{ bg: "gray.100" }}
                    />
                  </Flex>
                )}
              </Box>

              {/* ── Shelf Counts AG Grid ── */}
              <Box>
                <Text fontWeight={600} fontSize="sm" color="gray.700" mb={3}>
                  Shelf Counts
                </Text>
                <Box border={`1px solid ${BORDER}`} borderRadius="10px" overflow="hidden">
                  <AgGridWrapper
                    rowData={shelfRows}
                    columnDefs={shelfColumnDefs}
                    loading={false}
                    height="240px"
                    noRowsMessage="No shelf count data available."
                  />
                </Box>
              </Box>

            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// ─── Main component 
export default function ManualShelfCounts() {
  const { fromDate, toDate, setFromDate, setToDate } = useDateFilter("manualShelfCounts", {
    fromDate: getFirstDayOfMonth(),
    toDate:   getCurrentDate(),
  });

  const [summaryData, setSummaryData] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  // Detail modal state
  const [isOpen,         setIsOpen]         = useState(false);
  const [selectedRow,    setSelectedRow]    = useState(null);
  const [detailsData,    setDetailsData]    = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const toast = useToast();

  const columnDefs = buildColumnDefs();

  // ── Toast helpers ──────────────────────────────────────────────────────────
  const showError = useCallback((title, description) => {
    toast({
      title,
      description,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  }, [toast]);

  const showWarning = useCallback((title, description) => {
    toast({
      title,
      description,
      status: "warning",
      duration: 4000,
      isClosable: true,
      position: "top-right",
    });
  }, [toast]);

  // ── Error classification helper ────────────────────────────────────────────
  const classifyApiError = (err) => {
    if (!err) return "An unexpected error occurred.";
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    const status = err?.response?.status;
    if (status === 401 || status === 403) return "Unauthorized. Please log in again.";
    if (status === 404) return "Requested resource not found.";
    if (status >= 500) return "Server error. Please try again later.";
    if (!navigator.onLine) return "No internet connection detected.";
    return err?.response?.data?.message || err?.message || "An unexpected error occurred.";
  };

  // ── Fetch summary ──────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async (from, to) => {
    if (!from || !to) return;

    const rangeError = validateDateRange(from, to);
    if (rangeError) {
      setError(rangeError);
      showWarning("Invalid Date Range", rangeError);
      return;
    }

    setLoading(true);
    setSummaryData([]);
    setError("");

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS. MANUALSHELFCOUNTS.SUMMARY}`,
        buildDateRangeBody(from, to),
      );

      const data = Array.isArray(res?.data) ? res.data : [];
      setSummaryData(data);

      if (data.length === 0) {
        showWarning("No Records Found", "No records found for the selected date range.");
      }
    } catch (err) {
      const msg = classifyApiError(err);
      setError("Failed to load summary. " + msg);
      showError("Failed to Fetch Shelf Count Summary", msg);
    } finally {
      setLoading(false);
    }
  }, [showError, showWarning]);

  useEffect(() => {
    fetchSummary(fromDate || getFirstDayOfMonth(), toDate || getCurrentDate());
  }, []); 
 
  const handleFromDateChange = (val) => {
    setFromDate(val);
    if (val && toDate) fetchSummary(val, toDate);
  };

  const handleToDateChange = (val) => {
    setToDate(val);
    if (fromDate && val) fetchSummary(fromDate, val);
  };

  // ── View details handler ───────────────────────────────────────────────────
  const handleViewDetails = useCallback(async (row) => {
    const rangeError = validateDateRange(fromDate, toDate);
    if (rangeError) {
      showWarning("Invalid Date Range", rangeError);
      return;
    }

    setSelectedRow(row);
    setDetailsData([]);
    setIsOpen(true);
    setDetailsLoading(true);

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS. MANUALSHELFCOUNTS.DETAILS}/${row?.storeId}`,
        buildDateRangeBody(fromDate, toDate),
      );

      const data = Array.isArray(res?.data) ? res.data : [];
      setDetailsData(data);

      if (data.length === 0) {
        showWarning("No Image Details", "No image details found for this store in the selected date range.");
      }
    } catch (err) {
      const msg = classifyApiError(err);
      setDetailsData([]);
      showError("Failed to Fetch Shelf Count Details", msg);
    } finally {
      setDetailsLoading(false);
    }
  }, [fromDate, toDate, showError, showWarning]);

  const gridContext = { onViewDetails: handleViewDetails };

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      py={{ base: 6, md: 10 }}
      px={{ base: 3, sm: 4, md: 8 }}
      fontFamily="'Segoe UI', sans-serif"
    >
      {/* ── Filter bar ── */}
      <Flex
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3}
        mb={8}
        mt={6}
        justifyContent="flex-end"
      >
        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
        />
      </Flex>

      {/* ── Inline error banner ── */}
      {error && (
        <Flex
          align="center"
          gap={2}
          color="red.600"
          fontSize="sm"
          mb={4}
          fontWeight={500}
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="8px"
          px={4}
          py={3}
        >
          <WarningIcon />
          <Text>{error}</Text>
        </Flex>
      )}

      {/* ── Summary AG Grid ── */}
      <Box
        bg="white"
        borderRadius="12px"
        border={`2px solid ${BORDER}`}
        overflow="hidden"
        boxShadow="sm"
      >
        <AgGridWrapper
          rowData={summaryData}
          columnDefs={columnDefs}
          loading={loading}
          height="590px"
          extraGridProps={{ context: gridContext }}
          noRowsMessage={
            !fromDate || !toDate
              ? "Select a date range to load data."
              : "No records found for the selected date range."
          }
        />
      </Box>

      {/* ── Detail modal ── */}
      <DetailModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedRow={selectedRow}
        detailsLoading={detailsLoading}
        detailsData={detailsData}
        toDate={toDate}
      />
    </Box>
  );
}